<template>
  <section
    v-if="isCurrentItemOwner || description?.length || (!isHoverable && displayActionActive)"
    class="relative flex flex-col items-baseline gap-4 p-4 md:gap-16 md:flex-row bg-slate-50 dark:bg-zinc-800"
    :class="[!editMode ? 'hover:bg-slate-100 hover:dark:bg-zinc-700' : '', hasChapters ? 'sm:rounded-t-lg' : 'sm:rounded-lg']"
  >
    <h2 v-if="isCurrentItemOwner || description?.length" class="sr-only">Description</h2>

    <div
      v-if="!editMode && isCurrentItemOwner && !isProcessing"
      class="absolute z-0 inset-0 cursor-text"
      data-testid="viewer-description-click-area"
      @click.stop.prevent="onDescriptionClick"
    ></div>

    <div v-if="!isHoverable && displayActionActive" class="shrink-0">
      <CallToAction :hoverOnly="false" />
    </div>

    <SbTextLimiter
      v-if="!editMode && description?.length"
      class="description-content leading-normal max-w-none prose-sm prose dark:text-zinc-300 prose-slate dark:prose-invert"
      :class="{ 'opacity-50': isProcessing }"
    >
      <MarkdownWithScreenshots data-testid="viewer-description" :source="description" :linkify="true" :typographer="true" :breaks="true" />
    </SbTextLimiter>

    <div
      v-if="!editMode && !description?.length && isCurrentItemOwner"
      data-testid="viewer-description-empty-placeholder"
      class="dark:text-zinc-300 flex flex-row"
    >
      <Icon name="general.alignLeft" />
      <span>Type a description</span>
    </div>

    <div v-if="editMode" class="flex flex-col gap-2 w-full dark:text-zinc-300">
      <FormKit
        type="textarea"
        name="description"
        id="viewer-item-description"
        data-testid="viewer-description-edit-input"
        v-model="descriptionModel"
        auto-height
        validation-visibility="live"
        ref="descriptionField"
        @keydown.escape="exitEditMode"
        @keydown.enter.ctrl.exact="onSave"
        @keydown.enter.meta.exact="onSave"
      />
      <div class="flex justify-between">
        <div class="flex gap-2">
          <Button type="solid" data-testid="viewer-description-edit-save" variant="blue" icon="general.tick" @click.stop.prevent="onSave"
            >Save description</Button
          >
          <Button type="link" data-testid="viewer-description-edit-cancel" variant="black" @click.stop.prevent="exitEditMode">Cancel</Button>
        </div>
        <Button
          type="link"
          variant="black"
          href="https://support.zight.com/hc/en-us/articles/23315850688663-Basic-Markdown-Guide"
          target="_blank"
          icon="general.textFields"
          >Markdown is supported</Button
        >
      </div>
    </div>
  </section>
</template>
<script lang="ts" setup>
import { Ref, ref, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { SbTextLimiter, Icon, Button } from '@/storybook';
import CallToAction from './CallToAction.vue';
import MarkdownWithScreenshots from './MarkdownWithScreenshots.vue';

import { useCallToActionStore } from './../store/useCallToActionStore';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useAiMetadataStore } from '../store/useAiMetadataStore';
import { useToast } from '../../../composables/useToast';
import { useDebug } from '../../../composables/useDebug';
import { useUser } from '../composables/useUser';
import { nextTick } from 'process';
import { getNode } from '@formkit/core';
import { useConfirmModal } from '../../../storybook/modal/useModal';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { useSmartActionsStore } from '../store/useSmartActionsStore';

const itemStore = useViewerItemStore();
const transcriptionStore = useTranscriptionStore();
const { description } = storeToRefs(itemStore);
const descriptionModel: Ref<string> = ref(description.value || '');

const { error } = useToast();
const { debug } = useDebug();

const ctaStore = useCallToActionStore();
const { isHoverable, displayActionActive } = storeToRefs(ctaStore);

const aiMetadataStore = useAiMetadataStore();
const smartActionsStore = useSmartActionsStore();
const { hasChapters, isProcessing: isAiMetadataProcessing } = storeToRefs(aiMetadataStore);
const { isProcessing: isSmartActionProcessing } = storeToRefs(smartActionsStore);
const { isTranscriptionProcessing } = storeToRefs(transcriptionStore);
const isProcessing = computed(() => isAiMetadataProcessing.value || isTranscriptionProcessing.value || isSmartActionProcessing.value);

const { isCurrentItemOwner } = useUser();

const editMode: Ref<boolean> = ref(false);
const descriptionField = ref<HTMLTextAreaElement | null>(null);

const exitEditMode = async () => {
  const node = getNode('viewer-item-description');

  if (node && node.context?.state.dirty) {
    const { open } = useConfirmModal({
      title: 'Cancel & discard changes?',
      confirmText: 'Discard changes',
      cancelText: 'Back to editing',
    });

    const confirmed = await open();

    if (!confirmed) {
      return;
    }
  }

  descriptionModel.value = description.value || '';
  editMode.value = false;
};

watch(description, () => {
  if (editMode.value) {
    return;
  }

  descriptionModel.value = description.value || '';
});

const onSave = () => {
  editMode.value = false;

  if (descriptionModel.value === description.value) {
    return;
  }

  itemStore.updateDescription(descriptionModel.value).catch(e => {
    error('Cannot save description');
    debug('Cannot save description', e);
  });
};

const onDescriptionClick = () => {
  if (isCurrentItemOwner.value) {
    editMode.value = true;

    nextTick(() => {
      const input = document.getElementById('viewer-item-description') as HTMLTextAreaElement | null;

      if (!input) {
        return;
      }

      input.focus();
      input.selectionStart = input?.value.length;
    });
  }
};
</script>

<style>
.description-content [data-testid='viewer-description'] *:first-child {
  margin-top: 0;
}
</style>
